import React, { useState, useEffect, useRef } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Camera, AlertCircle, CheckCircle, Clock, X, Image } from "lucide-react";
import { usePermissions } from "../contexts/PermissionsContext";

const WalkthroughsPage = () => {
  const { can } = usePermissions();
  const [walkthroughs, setWalkthroughs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [selectedWalkthrough, setSelectedWalkthrough] = useState(null);
  const [selectedProject, setSelectedProject] = useState("all");
  const fileInputRef = useRef(null);
  
  const [form, setForm] = useState({
    project_id: "",
    notes: "",
  });

  const [issueForm, setIssueForm] = useState({
    description: "",
    assigned_to: "",
    status: "open",
  });

  const fetchData = async () => {
    try {
      const [walksRes, projectsRes, teamRes] = await Promise.all([
        API.get("/walkthroughs"),
        API.get("/projects"),
        API.get("/team"),
      ]);
      setWalkthroughs(walksRes.data);
      setProjects(projectsRes.data);
      setTeam(teamRes.data);
    } catch (error) {
      toast.error("Failed to load walkthroughs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateWalkthrough = async (e) => {
    e.preventDefault();
    try {
      await API.post("/walkthroughs", form);
      toast.success("Walkthrough created");
      setDialogOpen(false);
      setForm({ project_id: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create walkthrough");
    }
  };

  const handlePhotoUpload = async (walkthroughId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await API.post(`/walkthroughs/${walkthroughId}/photos`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload photo");
    }
  };

  const handleAddIssue = async (e) => {
    e.preventDefault();
    if (!selectedWalkthrough) return;
    try {
      await API.post(`/walkthroughs/${selectedWalkthrough.walkthrough_id}/issues`, issueForm);
      toast.success("Issue added");
      setIssueDialogOpen(false);
      setIssueForm({ description: "", assigned_to: "", status: "open" });
      fetchData();
    } catch (error) {
      toast.error("Failed to add issue");
    }
  };

  const handleUpdateIssue = async (walkthroughId, issueId, status) => {
    try {
      await API.put(`/walkthroughs/${walkthroughId}/issues/${issueId}`, { status });
      toast.success("Issue updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update issue");
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.project_id === projectId);
    return project?.name || "Unknown";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "open":
        return <AlertCircle size={14} className="text-red-500" />;
      case "in_progress":
        return <Clock size={14} className="text-amber-500" />;
      case "resolved":
        return <CheckCircle size={14} className="text-emerald-500" />;
      default:
        return <AlertCircle size={14} className="text-slate-400" />;
    }
  };

  const filteredWalkthroughs = selectedProject && selectedProject !== "all"
    ? walkthroughs.filter((w) => w.project_id === selectedProject)
    : walkthroughs;

  if (loading) {
    return <div className="animate-pulse">Loading walkthroughs...</div>;
  }

  return (
    <div data-testid="walkthroughs-page" className="space-y-6 animate-slide-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">Jobsite Walkthroughs</h1>
          <p className="text-slate-400 font-ibm mt-1">Document site visits, photos, and issues</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-48 bg-slate-900 border-slate-700 text-slate-300" data-testid="filter-project-select">
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-slate-200">All Projects</SelectItem>
              {projects.map((project) => (
                <SelectItem key={project.project_id} value={project.project_id} className="text-slate-200">
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {can('create_walkthrough') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-walkthrough-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                  <Plus size={18} className="mr-2" />
                  New Walkthrough
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="font-chivo text-white">New Walkthrough</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateWalkthrough} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-400">Project *</label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger data-testid="walkthrough-project-select" className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue placeholder="Select a project" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {projects.map((project) => (
                        <SelectItem key={project.project_id} value={project.project_id} className="text-slate-200">
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-400">Notes</label>
                  <textarea
                    data-testid="walkthrough-notes-input"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    rows={4}
                    placeholder="Walkthrough notes..."
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-walkthrough-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                    Create Walkthrough
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Walkthroughs List */}
      {filteredWalkthroughs.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
          <Camera size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No walkthroughs yet</h3>
          <p className="text-slate-500 mb-4">Create your first walkthrough to document a site visit</p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredWalkthroughs.map((walk) => (
            <div
              key={walk.walkthrough_id}
              data-testid={`walkthrough-card-${walk.walkthrough_id}`}
              className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden"
            >
              <div className="p-6 border-b border-slate-800">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-chivo font-semibold text-lg text-white">
                      {getProjectName(walk.project_id)}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {new Date(walk.logged_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handlePhotoUpload(walk.walkthrough_id, e.target.files[0]);
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`upload-photo-${walk.walkthrough_id}`}
                      onClick={() => {
                        fileInputRef.current?.click();
                      }}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    >
                      <Camera size={14} className="mr-1" />
                      Add Photo
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`add-issue-${walk.walkthrough_id}`}
                      onClick={() => {
                        setSelectedWalkthrough(walk);
                        setIssueDialogOpen(true);
                      }}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    >
                      <AlertCircle size={14} className="mr-1" />
                      Add Issue
                    </Button>
                  </div>
                </div>
                {walk.notes && <p className="text-slate-400 mt-3">{walk.notes}</p>}
              </div>

              {/* Photos */}
              {walk.photos?.length > 0 && (
                <div className="p-6 border-b border-slate-800">
                  <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                    <Image size={16} />
                    Photos ({walk.photos.length})
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {walk.photos.map((photo, idx) => (
                      <img
                        key={idx}
                        src={photo}
                        alt={`Site photo ${idx + 1}`}
                        className="w-full h-24 object-cover rounded-md border border-slate-700"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Issues */}
              {walk.issues?.length > 0 && (
                <div className="p-6">
                  <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                    <AlertCircle size={16} />
                    Issues ({walk.issues.length})
                  </h4>
                  <div className="space-y-2">
                    {walk.issues.map((issue) => (
                      <div
                        key={issue.issue_id}
                        className="flex items-center justify-between p-3 bg-slate-800 rounded-md border border-slate-700"
                      >
                        <div className="flex items-center gap-3">
                          {getStatusIcon(issue.status)}
                          <span className="text-sm text-slate-300">{issue.description}</span>
                        </div>
                        <Select
                          value={issue.status}
                          onValueChange={(v) => handleUpdateIssue(walk.walkthrough_id, issue.issue_id, v)}
                        >
                          <SelectTrigger className="w-32 h-8 text-xs bg-slate-800 border-slate-700 text-slate-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value="open" className="text-slate-200">Open</SelectItem>
                            <SelectItem value="in_progress" className="text-slate-200">In Progress</SelectItem>
                            <SelectItem value="resolved" className="text-slate-200">Resolved</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Issue Dialog */}
      <Dialog open={issueDialogOpen} onOpenChange={setIssueDialogOpen}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="font-chivo text-white">Add Issue</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddIssue} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-400">Description *</label>
              <textarea
                data-testid="issue-description-input"
                value={issueForm.description}
                onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })}
                required
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                rows={3}
                placeholder="Describe the issue..."
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-400">Assign To</label>
              <Select value={issueForm.assigned_to} onValueChange={(v) => setIssueForm({ ...issueForm, assigned_to: v })}>
                <SelectTrigger data-testid="issue-assign-select" className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {team.map((member) => (
                    <SelectItem key={member.member_id} value={member.member_id} className="text-slate-200">
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIssueDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                Cancel
              </Button>
              <Button type="submit" data-testid="save-issue-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                Add Issue
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WalkthroughsPage;
